import { DecimalPipe, PercentPipe } from '@angular/common';
import { Component, ElementRef, HostListener, inject, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ChartConfiguration } from 'chart.js';
import dayjs, { Dayjs } from 'dayjs/esm';
import moment from 'moment';
import { BaseChartDirective } from 'ng2-charts';
import { NgxDaterangepickerMd } from 'ngx-daterangepicker-material';
import { ToastrService } from 'ngx-toastr';
import { catchError, EMPTY, expand, finalize, forkJoin, Observable, of, reduce } from 'rxjs';
import { AgenciesService } from '../../../orval-gen/endpoints/agencies/agencies.service';
import { OrdersService } from '../../../orval-gen/endpoints/orders/orders.service';
import { ProvidersService } from '../../../orval-gen/endpoints/providers/providers.service';
import { StatsService } from '../../../orval-gen/endpoints/stats/stats.service';
import {
  AgencyConsumerAgenciesList200DataItem as Agency,
  AgencyProvidersList200DataItem,
  AgencyStatsBreakdown200DataItem as SummaryItem,
  AgencyStatsTimeline200DataItem as TimelineItem,
} from '../../../orval-gen/models';
import { baseLineOptions, hoverLinePlugin, STATUS_COLORS } from '../../shared/chart-theme';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { LocalStorageService } from '../../shared/services/local-storage.service';
import { unwrap } from '../../utils/http-utils';

type GroupBy = 'provider' | 'agent' | 'application' | 'agency';
type RangeBy = 'day' | 'week' | 'month' | 'year';
type QuickRange = '30d' | 'month' | '12m';

/**
 * A date of the selected range. The picker hands back Day.js dates while the quick ranges and the URL build Moment
 * ones; the screen only ever formats them, which both do alike.
 */
interface RangeDate {
  format(template: string): string;
}

const GROUP_BY: readonly GroupBy[] = ['provider', 'agent', 'application', 'agency'];
const RANGE_BY: readonly RangeBy[] = ['day', 'week', 'month', 'year'];

/** Order statuses in display order, with the totals key and the hub's count and timeline type behind each. */
export const STATUSES = [
  { label: 'Booked', key: 'orders_created', type: 'created' },
  { label: 'Issued', key: 'orders_issued', type: 'issued' },
  { label: 'Voided', key: 'orders_voided', type: 'voided' },
  { label: 'Expired', key: 'orders_expired', type: 'expired' },
  { label: 'Cancelled', key: 'orders_cancelled', type: 'cancelled' },
] as const;

type StatusKey = (typeof STATUSES)[number]['key'];

interface TableRow {
  name: string;
  code?: string;
  counts: Record<StatusKey, number>;
}

interface TableGroup {
  application: string | null;
  rows: TableRow[];
  total: Record<StatusKey, number> | null;
}

@Component({
  selector: 'app-reports',
  imports: [
    DecimalPipe,
    PercentPipe,
    BaseChartDirective,
    NgxDaterangepickerMd,
    FormsModule,
    ReactiveFormsModule,
    IconComponent,
  ],
  templateUrl: './reports.component.html',
  styleUrl: './reports.component.scss',
})
export class ReportsComponent implements OnInit {
  private readonly statsService = inject(StatsService);
  private readonly agenciesService = inject(AgenciesService);
  private readonly providersService = inject(ProvidersService);
  private readonly ordersService = inject(OrdersService);
  private readonly toastr = inject(ToastrService);
  private readonly fb = inject(FormBuilder);
  private readonly ls = inject(LocalStorageService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly el = inject(ElementRef);

  readonly statuses = STATUSES;
  readonly statusColors = STATUS_COLORS;

  loaded = false;
  loadingSummary = false;
  loadingBreakdown = false;
  /** Managers of the consumer's super agency, the only ones who may filter by agency or export. */
  isConsumerOwner = false;
  selectedGroup: GroupBy = 'provider';
  selectedRange: RangeBy = 'month';
  pendingAgencyId: string | null = null;
  selectedAgency: Agency | null = null;
  agencies: Agency[] = [];
  airlineNames: Record<string, string> = {};
  statsSummaryData: SummaryItem[] = [];
  tableGroups: TableGroup[] = [];
  totals: Record<StatusKey, number> = this.emptyCounts();
  isExportDropdownOpen = false;
  activeQuickRange: QuickRange | null = '30d';

  // Day.js, as the picker's ranges input takes.
  ranges: Record<string, [Dayjs, Dayjs]> = {
    Today: [dayjs(), dayjs()],
    Yesterday: [dayjs().subtract(1, 'days'), dayjs().subtract(1, 'days')],
    'Last 7 Days': [dayjs().subtract(6, 'days'), dayjs()],
    'Last 30 Days': [dayjs().subtract(29, 'days'), dayjs()],
    'This Month': [dayjs().startOf('month'), dayjs().endOf('month')],
    'Last Month': [
      dayjs().subtract(1, 'month').startOf('month'),
      dayjs().subtract(1, 'month').endOf('month'),
    ],
    'This Year': [dayjs().startOf('year'), dayjs().endOf('year')],
    'Last Year': [dayjs().subtract(1, 'year').startOf('year'), dayjs().startOf('year')],
  };
  selectedDateRange: { startDate: RangeDate; endDate: RangeDate } = {
    startDate: moment().subtract(29, 'days'),
    endDate: moment(),
  };

  readonly chartPlugins = [hoverLinePlugin];
  lineChartOptions = baseLineOptions();
  lineChartData: ChartConfiguration<'line'>['data'] = { labels: [], datasets: [] };

  emailForm!: FormGroup;
  recipientEmails: string[] = [];
  selectedRecipientEmails: string[] = [];

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent): void {
    const pop = this.el.nativeElement.querySelector('.export-pop');
    if (this.isExportDropdownOpen && pop && !pop.contains(event.target as Node)) {
      this.isExportDropdownOpen = false;
    }
  }

  ngOnInit(): void {
    this.isConsumerOwner = this.ls.getBool('isConsumerOwner');
    this.buildEmailForm();
    this.parseUrl();
    this.loadStatsWithMappings();
  }

  // ---------- Data loading ----------

  private get windowParams(): { date_start: string; date_end: string; agency_id?: string } {
    return {
      date_start: this.selectedDateRange?.startDate?.format('YYYY-MM-DD'),
      date_end: this.selectedDateRange?.endDate?.format('YYYY-MM-DD'),
      agency_id: this.selectedAgency?.id || this.pendingAgencyId || undefined,
    };
  }

  /**
   * The per-entity totals behind the KPIs and the table, from the hub's breakdown (the legacy summary). It pages at
   * 50 rows, which a large consumer's agents can exceed, so every page is read.
   */
  private fetchStatsSummary(): Observable<SummaryItem[]> {
    const page = (n: number) =>
      this.statsService.agencyStatsBreakdown({
        ...this.windowParams,
        group_by: this.selectedGroup || 'provider',
        page: n,
        limit: 50,
      });
    return page(1).pipe(
      // expand projects one response per page, so index i is page i + 1.
      expand((res, i) => (i + 2 <= res.metadata.total_pages ? page(i + 2) : EMPTY)),
      reduce((rows, res) => rows.concat(res.data), [] as SummaryItem[])
    );
  }

  /** The bucketed totals behind the chart, from the hub's timeline (the legacy breakdown). */
  private fetchStatsBreakdown(): Observable<TimelineItem[]> {
    return this.statsService
      .agencyStatsTimeline({ ...this.windowParams, interval: this.selectedRange })
      .pipe(unwrap());
  }

  loadStatsWithMappings(): void {
    this.loaded = false;

    const requests: {
      statsSummary: Observable<SummaryItem[]>;
      statsBreakdown: Observable<TimelineItem[]>;
      providers: Observable<AgencyProvidersList200DataItem[]>;
      agencies?: Observable<Agency[]>;
    } = {
      statsSummary: this.fetchStatsSummary().pipe(
        catchError(error => {
          console.error('Failed to fetch stats summary:', error);
          return of([]);
        })
      ),
      statsBreakdown: this.fetchStatsBreakdown().pipe(
        catchError(error => {
          console.error('Failed to fetch stats breakdown:', error);
          return of([]);
        })
      ),
      // Only used to show airline names next to provider codes in the breakdown table.
      providers: this.providersService.agencyProvidersList().pipe(
        unwrap(),
        catchError(error => {
          console.error('Failed to fetch providers:', error);
          return of([]);
        })
      ),
    };

    if (this.isConsumerOwner) {
      // The route's maximum page size, so the filter lists every agency of the consumer in one call.
      requests.agencies = this.agenciesService.agencyConsumerAgenciesList({ limit: 100 }).pipe(
        unwrap(),
        catchError(error => {
          console.error('Failed to fetch agencies:', error);
          return of([]);
        })
      );
    }

    forkJoin(requests)
      .pipe(finalize(() => (this.loaded = true)))
      .subscribe(({ statsSummary, statsBreakdown, providers, agencies }) => {
        if (this.isConsumerOwner && agencies) {
          this.agencies = agencies;

          if (this.pendingAgencyId) {
            this.selectedAgency = this.agencies?.find(a => a.id === this.pendingAgencyId) ?? null;
            this.pendingAgencyId = null;

            if (!this.selectedAgency) {
              // Remove invalid agencyId from URL
              this.router.navigate([], {
                relativeTo: this.route,
                queryParams: { agencyId: null },
                queryParamsHandling: 'merge',
              });
              this.loadStatsWithMappings();
              return;
            }
          }
        }

        this.airlineNames = Object.fromEntries(
          (providers ?? []).map(p => [p.provider.agw_id, p.provider.name])
        );
        this.applySummary(statsSummary);
        this.generateLineChartData(statsBreakdown);
      });
  }

  loadStatsSummaryOnly(): void {
    this.loadingSummary = true;
    this.fetchStatsSummary()
      .pipe(finalize(() => (this.loadingSummary = false)))
      .subscribe({
        next: statsSummary => this.applySummary(statsSummary),
        error: error => console.error('Failed to fetch stats summary:', error),
      });
  }

  loadStatsBreakdownOnly(): void {
    this.loadingBreakdown = true;
    this.fetchStatsBreakdown()
      .pipe(finalize(() => (this.loadingBreakdown = false)))
      .subscribe({
        next: statsBreakdown => this.generateLineChartData(statsBreakdown),
        error: error => console.error('Failed to fetch stats breakdown:', error),
      });
  }

  // ---------- Summary: KPIs, distribution, table ----------

  private applySummary(statsSummary: SummaryItem[]): void {
    this.statsSummaryData = statsSummary ?? [];
    this.totals = this.sumCounts(this.statsSummaryData);
    this.tableGroups = this.buildTableGroups(this.statsSummaryData);
  }

  private emptyCounts(): Record<StatusKey, number> {
    return {
      orders_created: 0,
      orders_issued: 0,
      orders_voided: 0,
      orders_expired: 0,
      orders_cancelled: 0,
    };
  }

  private countsOf(item: SummaryItem): Record<StatusKey, number> {
    const counts = this.emptyCounts();
    for (const s of STATUSES) counts[s.key] = Number(item.counts[s.type]) || 0;
    return counts;
  }

  private sumCounts(items: SummaryItem[]): Record<StatusKey, number> {
    const totals = this.emptyCounts();
    for (const item of items) {
      for (const s of STATUSES) totals[s.key] += Number(item.counts[s.type]) || 0;
    }
    return totals;
  }

  /** A null entity is the hub's bucket for orders the grouping does not apply to, or of a deleted entity. */
  private rowOf(item: SummaryItem): TableRow {
    const counts = this.countsOf(item);
    const name = item.entity?.name;
    if (this.selectedGroup === 'provider') {
      return {
        name: (name && this.airlineNames[name]) || name || 'Unattributed',
        code: name,
        counts,
      };
    }
    return { name: name || 'Unattributed', counts };
  }

  /**
   * On the provider grouping, one group per application, rows sorted by Booked desc, with a TOTAL
   * row when a group has more than one row. The other groupings carry no application, so they yield
   * a single flat group (no group headings).
   */
  private buildTableGroups(stats: SummaryItem[]): TableGroup[] {
    const byBooked = (a: SummaryItem, b: SummaryItem) => b.counts.created - a.counts.created;

    if (this.selectedGroup !== 'provider') {
      const items = [...stats].sort(byBooked);
      return items.length
        ? [
            {
              application: null,
              rows: items.map(i => this.rowOf(i)),
              total: items.length > 1 ? this.sumCounts(items) : null,
            },
          ]
        : [];
    }

    const grouped = new Map<string, SummaryItem[]>();
    for (const item of stats) {
      const appId = item.application?.id ?? 'unknown';
      if (!grouped.has(appId)) grouped.set(appId, []);
      grouped.get(appId)!.push(item);
    }

    return Array.from(grouped.values()).map(items => {
      items.sort(byBooked);
      return {
        application: items[0]?.application?.name || 'N/A',
        rows: items.map(i => this.rowOf(i)),
        total: items.length > 1 ? this.sumCounts(items) : null,
      };
    });
  }

  /** Share of booked orders for a status (0–1); 0 when nothing was booked. */
  shareOfBooked(key: StatusKey): number {
    return this.totals.orders_created ? this.totals[key] / this.totals.orders_created : 0;
  }

  /** Booked orders not (yet) in any other status shown here. */
  get otherCount(): number {
    const t = this.totals;
    return Math.max(
      0,
      t.orders_created - t.orders_issued - t.orders_voided - t.orders_expired - t.orders_cancelled
    );
  }

  get otherShare(): number {
    return this.totals.orders_created ? this.otherCount / this.totals.orders_created : 0;
  }

  /** Statuses for the distribution bar and list, largest first (Booked itself excluded). */
  get distribution(): { label: string; count: number; share: number; color: string }[] {
    return STATUSES.filter(s => s.key !== 'orders_created')
      .map(s => ({
        label: s.label,
        count: this.totals[s.key],
        share: this.shareOfBooked(s.key),
        color: STATUS_COLORS[s.label],
      }))
      .sort((a, b) => b.count - a.count);
  }

  get distributionLabel(): string {
    return this.distribution
      .map(d => `${d.label} ${(d.share * 100).toFixed(1)}%`)
      .concat(this.otherCount ? [`Other ${(this.otherShare * 100).toFixed(1)}%`] : [])
      .join(', ');
  }

  get listingLabel(): string {
    return { provider: 'provider', agent: 'agent', application: 'application', agency: 'agency' }[
      this.selectedGroup
    ];
  }

  // ---------- Line chart ----------

  generateLineChartData(statsBreakdown: TimelineItem[]): void {
    const labelMap = new Map<string, string>();
    const typeMap = new Map<string, Map<string, number>>();

    for (const stat of statsBreakdown ?? []) {
      // Each bucket is its period's first day, so it also sorts the labels.
      const labelKey = stat.bucket;
      let labelDisplay: string;

      switch (this.selectedRange) {
        case 'day':
          labelDisplay = stat.bucket;
          break;
        case 'year':
          labelDisplay = stat.bucket.slice(0, 4);
          break;
        case 'month':
          labelDisplay = this.formatMonthWithYear(stat.bucket);
          break;
        case 'week':
          labelDisplay = moment(stat.bucket).format('GGGG [W]WW');
          break;
        default:
          continue;
      }

      labelMap.set(labelKey, labelDisplay);

      const status = STATUSES.find(s => s.type === stat.type);
      if (!status) continue;

      if (!typeMap.has(status.label)) typeMap.set(status.label, new Map());
      const typeData = typeMap.get(status.label)!;
      typeData.set(labelKey, (typeData.get(labelKey) || 0) + stat.count);
    }

    const sortedKeys = Array.from(labelMap.keys()).sort((a, b) => a.localeCompare(b));

    this.lineChartData = {
      labels: sortedKeys.map(key => labelMap.get(key)!),
      datasets: STATUSES.map(s => {
        const values = typeMap.get(s.label) ?? new Map<string, number>();
        return {
          label: s.label,
          data: sortedKeys.map(key => values.get(key) ?? 0),
          borderColor: STATUS_COLORS[s.label],
          backgroundColor: STATUS_COLORS[s.label],
          pointHoverBorderColor: STATUS_COLORS[s.label],
          pointBackgroundColor: '#fff',
          fill: false,
        };
      }),
    };
  }

  formatMonthWithYear(dateStr: string): string {
    const [year, month] = dateStr.split('-');
    const shortMonth = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ][+month - 1];
    return `${shortMonth} ${year.slice(2)}`;
  }

  // ---------- Filters ----------

  onDateRangeChange(
    event: { startDate: RangeDate | null; endDate: RangeDate | null } | null,
    quick: QuickRange | null = null
  ): void {
    if (event?.startDate && event?.endDate) {
      this.activeQuickRange = quick;
      this.selectedDateRange = { startDate: event.startDate, endDate: event.endDate };

      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {
          from: event.startDate.format('YYYY-MM-DD'),
          to: event.endDate.format('YYYY-MM-DD'),
        },
        queryParamsHandling: 'merge',
      });

      this.loadStatsSummaryOnly();
      this.loadStatsBreakdownOnly();
    }
  }

  setQuickRange(quick: QuickRange): void {
    const ranges: Record<QuickRange, [moment.Moment, moment.Moment]> = {
      '30d': [moment().subtract(29, 'days'), moment()],
      month: [moment().startOf('month'), moment().endOf('month')],
      '12m': [moment().subtract(12, 'months').add(1, 'day'), moment()],
    };
    const [startDate, endDate] = ranges[quick];
    this.onDateRangeChange({ startDate, endDate }, quick);
  }

  selectRange(value: RangeBy): void {
    this.selectedRange = value;
    this.loadStatsBreakdownOnly();
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { range: this.selectedRange },
      queryParamsHandling: 'merge',
    });
  }

  selectGroup(value: GroupBy): void {
    this.selectedGroup = value;
    this.loadStatsSummaryOnly();
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { group: this.selectedGroup },
      queryParamsHandling: 'merge',
    });
  }

  selectAgencyById(id: string): void {
    this.selectAgency(this.agencies.find(a => a.id === id) ?? null);
  }

  selectAgency(agency: Agency | null): void {
    this.selectedAgency = agency;
    this.loadStatsSummaryOnly();
    this.loadStatsBreakdownOnly();
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { agencyId: agency?.id ?? null },
      queryParamsHandling: 'merge',
    });
  }

  // ---------- Export ----------

  buildEmailForm(): void {
    this.emailForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
    });
    this.setPassengerEmails();
  }

  setPassengerEmails(): void {
    this.recipientEmails = [];
    this.selectedRecipientEmails = [];
    const email = this.ls.getItem('email');
    if (email) {
      this.recipientEmails.push(email);
    }
  }

  addEmail(): void {
    const email = this.emailForm.get('email')?.value;
    if (!email || this.recipientEmails.includes(email)) {
      return;
    }
    this.recipientEmails.push(email);
    this.selectedRecipientEmails.push(email);
    this.emailForm.reset();
  }

  onPassengerEmailsChange(email: string): void {
    if (!this.selectedRecipientEmails.includes(email)) {
      this.selectedRecipientEmails.push(email);
    } else {
      this.selectedRecipientEmails.splice(this.selectedRecipientEmails.indexOf(email), 1);
    }
  }

  export(): void {
    this.ordersService
      .agencyOrdersExport({
        issuing_date_from: this.selectedDateRange?.startDate?.format('YYYY-MM-DD'),
        issuing_date_to: this.selectedDateRange?.endDate?.format('YYYY-MM-DD'),
        recipient_emails: this.selectedRecipientEmails,
      })
      .subscribe({
        next: () => {
          this.isExportDropdownOpen = false;
          this.toastr.success('The export will be emailed shortly.', 'Success');
        },
        error: err => console.error('Export failed:', err),
      });
  }

  // ---------- URL state ----------

  parseUrl(): void {
    const params = { ...this.route.snapshot.queryParams };

    const from = params['from'];
    const to = params['to'];
    if (from && to) {
      this.selectedDateRange = { startDate: moment(from), endDate: moment(to) };
      this.activeQuickRange = null;
    }

    const range = params['range'];
    this.selectedRange = RANGE_BY.includes(range) ? range : 'month';

    const group = params['group'];
    this.selectedGroup = GROUP_BY.includes(group) ? group : 'provider';

    this.pendingAgencyId = params['agencyId'] ?? null;
  }
}
